import { searchApiBasePath } from '../../config/baseConfig';

const req = require('request-promise');

export interface AllSpaceSearchParams {
  q?: string;
  limit?: number;
  cursor?: string;
  id?: string[];
  prefecture?: string[];
  postcode?: string;
  city?: string;
  address?: string;
  buildingName?: string;
  customerKanaName?: string;
  customerPhoneNumber?: string;
  attributes?: string[];
  status?: string[];
  sumamoruLinkable?: string;
  sumamoruProjectTypes?: string[];
  contractApplicationStatus?: string[];
  contractSumamoruStatus?: string[];
  contractSumamoruStatusNot?: string[];
  contractVacateDateBefore?: string;
  contractOccupyScheduledDateFrom?: string;
  contractOccupyScheduledDateTo?: string;
}

export interface InstalledDevice {
  installationDate: string;
  model: string;
}

export interface SumamoruContractItem {
  isCurrent: boolean;
  contractId: string;
  contractStartDate: number;
  occupyScheduledDate?: number;
  vacateDate?: number;
  hasElectricContract: boolean;
  applicationStatus: string;
  sumamoruStatus: string;
  sumamoruPlan?: string;
  sumamoruAdditionalServices: string[];
  customerName: string;
  customerKanaName: string;
  customerPhoneNumber: string;
  customerEmail: string;
  linkageId: string;
  sumamoruProjectType?: string;
  viewnUrl?: string;
  viewnContractId?: number;
  appliedDate?: number;
  applicationUrlPublishedDate?: number;
  installedDevices?: InstalledDevice[];
}

export interface SumamoruAddOnData {
  sumamoruLinkable: boolean;
  sumamoruProjectTypes: string[];
  sumamoruCode: string;
  sumamoruPlans: string[];
  sumamoruAdditionalServices: string[];
  sumamoruInstallAppTypes: string[];
}

export interface AllSpaceSearchItem {
  id: string;
  organizationId: string;
  name: string;
  phoneticName?: string;
  code?: string;
  isGroup: boolean;
  postcode?: string;
  prefecture?: string;
  city?: string;
  afterCity?: string;
  address: string;
  bitlockIds: string[];
  hasDevices?: boolean;
  parents: string[];
  parentNames: string[];
  parentId?: string;
  parentName?: string;
  attributes: string[];
  createdAt?: number;
  status?: string;
  equipments: string[];
  memo?: string;
  occupantContractIds: string[];
  occupantCustomerIds: string[];
  occupantRepresentativeNames: string[];
  charge?: number;
  deposit?: number;
  depositUnit?: string;
  keyMoney?: number;
  keyMoneyUnit?: string;
  managementFee?: number;
  brokerageFee?: number;
  ownerName?: string;
  ownerPhoneNumber?: string;
  layout?: string;
  exclusiveArea?: number;
  floorNumber?: number;
  constructDate?: number;
  nearestStation?: string;
  storyNumber?: number;
  structure?: string;
  totalPropertyCount?: number;
  occupiedCount?: number;
  showingPropertyCount?: number;
  vacantStartDate?: number;
  sumamoruData?: SumamoruAddOnData & {
    contracts: SumamoruContractItem[];
  };
  addOns?: {
    sumamoruData: SumamoruAddOnData;
  };
  [key: string]: unknown;
}

export interface SpaceContractData {
  base: AllSpaceSearchItem;
  sumamoruData: SumamoruAddOnData & {
    contracts: SumamoruContractItem[];
  };
  contract: SumamoruContractItem;
}

export interface AllSpaceSearchItemWithContracts extends AllSpaceSearchItem {
  sumamoruData: SumamoruAddOnData & {
    contracts: SumamoruContractItem[];
  };
}

export const hasContracts = (hit: AllSpaceSearchItem): hit is AllSpaceSearchItemWithContracts =>
  !!hit.sumamoruData && !!hit.sumamoruData.contracts && hit.sumamoruData.contracts.length > 0;

export interface AllSpaceSearchResponse {
  results: AllSpaceSearchItem[];
  nextCursor: string;
}

/**
 * search-api の allSpaces エンドポイントを呼び出す
 */
export const searchAllSpaces = async (
  accessToken: string,
  params: AllSpaceSearchParams
): Promise<AllSpaceSearchResponse> => {
  const queryParams = new URLSearchParams();

  if (params.q) queryParams.append('q', params.q);
  if (params.limit !== undefined) queryParams.append('limit', String(params.limit));
  if (params.cursor) queryParams.append('cursor', params.cursor);

  if (params.id && params.id.length > 0) {
    params.id.forEach(v => queryParams.append('id', v));
  }
  if (params.prefecture && params.prefecture.length > 0) {
    params.prefecture.forEach(v => queryParams.append('prefecture', v));
  }
  if (params.postcode) {
    queryParams.append('postcode', params.postcode);
  }
  if (params.city) {
    queryParams.append('city', params.city);
  }
  if (params.address) {
    queryParams.append('address', params.address);
  }
  if (params.buildingName) {
    queryParams.append('buildingName', params.buildingName);
  }
  if (params.customerKanaName) {
    queryParams.append('customerKanaName', params.customerKanaName);
  }
  if (params.customerPhoneNumber) {
    queryParams.append('customerPhoneNumber', params.customerPhoneNumber);
  }
  if (params.attributes && params.attributes.length > 0) {
    params.attributes.forEach(v => queryParams.append('attributes', v));
  }
  if (params.status && params.status.length > 0) {
    params.status.forEach(v => queryParams.append('status', v));
  }
  if (params.sumamoruLinkable !== undefined) {
    queryParams.append('sumamoruLinkable', params.sumamoruLinkable);
  }
  if (params.sumamoruProjectTypes && params.sumamoruProjectTypes.length > 0) {
    params.sumamoruProjectTypes.forEach(v => queryParams.append('sumamoruProjectTypes', v));
  }
  if (params.contractApplicationStatus && params.contractApplicationStatus.length > 0) {
    params.contractApplicationStatus.forEach(v => queryParams.append('contractApplicationStatus', v));
  }
  if (params.contractSumamoruStatus && params.contractSumamoruStatus.length > 0) {
    params.contractSumamoruStatus.forEach(v => queryParams.append('contractSumamoruStatus', v));
  }
  if (params.contractSumamoruStatusNot && params.contractSumamoruStatusNot.length > 0) {
    params.contractSumamoruStatusNot.forEach(v => queryParams.append('contractSumamoruStatusNot', v));
  }
  if (params.contractVacateDateBefore) {
    queryParams.append('contractVacateDateBefore', params.contractVacateDateBefore);
  }
  if (params.contractOccupyScheduledDateFrom) {
    queryParams.append('contractOccupyScheduledDateFrom', params.contractOccupyScheduledDateFrom);
  }
  if (params.contractOccupyScheduledDateTo) {
    queryParams.append('contractOccupyScheduledDateTo', params.contractOccupyScheduledDateTo);
  }

  const url = `${searchApiBasePath}homehubSearchApi/search/allSpaces?${queryParams.toString()}`;

  const options = {
    method: 'GET' as const,
    uri: url,
    timeout: 30 * 1000,
    headers: {
      'x-api-key': accessToken
    },
    json: true
  };

  const response: AllSpaceSearchResponse = await req(options);
  return response;
};

export const browseAllSpaces = async (
  accessToken: string,
  params: AllSpaceSearchParams
): Promise<AllSpaceSearchItem[]> => {
  const allResults: AllSpaceSearchItem[] = [];
  let cursor: string | undefined = undefined;

  while (true) {
    const response = await searchAllSpaces(accessToken, { ...params, cursor });
    allResults.push(...response.results);

    if (!response.nextCursor) {
      break;
    }
    cursor = response.nextCursor;
  }

  return allResults;
};
